<template>
  <div class="form-group mb-3">
    <label v-if="label" :class="{ 'text-danger': hasError }">
      {{ label }}
    </label>
    
    <div :class="{ 'input-group': prefixText || suffixText }">
      <div v-if="prefixText" class="input-group-prepend">
        <div class="input-group-text prefix-addon font-weight-bold">{{ prefixText }}</div>
      </div>
      
      <input
        :type="type"
        :class="[inputClass, 'form-control flex-grow-1 custom-input shadow_inset_sm', { 'is-invalid': hasError }, { 'w-100': !prefixText && !suffixText, 'has-prefix': prefixText, 'has-suffix': suffixText }]"
        :value="displayValue"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        :maxlength="maxLength"
        v-bind="$attrs"
        @input="onInput"
        @keydown="onKeyDown"
        @keyup.enter="onEnter"
        @blur="onBlur"
      />
      
      <div v-if="suffixText" class="input-group-append">
        <div class="input-group-text suffix-addon font-weight-bold">{{ suffixText }}</div>
      </div>
    </div>
    
    <small v-if="hasError" class="form-text text-danger">
      {{ errorMessage }}
    </small>
  </div>
</template>
<script>
/**
 * /--------------------------/
 * COMPONENTE: TextInput 
 * /--------------------------/
 * 
 * Este Componente es un Input Text modularizado que soporta:
 * - Validaciones visuales de Bootstrap
 * - Tipos dinámicos (text, password, email, etc.)
 * - Eventos estandarizados
 * 
 * --- INSTRUCCIONES DE USO POR PROP ---
 * 
 * @prop {String} type
 *       Uso: `:type="'password'"` (Default: 'text')
 * 
 * @prop {String} inputClass
 *       Uso: `:inputClass="'form-control-sm'"`
 *       Efecto: Clases adicionales para el input.
 * 
 * @prop {Number|String} maxLength
 *       Uso: `:max-length="50"`
 *       Efecto: Limita la cantidad de caracteres.
 * 
 * @prop {String} placeholder
 *       Uso: `:placeholder="'Escriba aquí...'"`
 *       Efecto: Muestra un texto de ayuda cuando el input está vacío.
 * 
 * @prop {Boolean} hasError / @prop {String} errorMessage
 *       Uso: Para mostrar estados de error.
 *       Efecto: Cambia el color del borde a rojo y muestra el mensaje inferior.
 */

export default {
  name: 'InputProvider',
  props: {
    // Texto del label
    label: { 
      type: String, 
      default: '' 
    },
    // Valor para v-model
    value: { 
      type: [String, Number], 
      default: '' 
    },
    // Tipo de input: text, password, email, number, etc.
    type: { 
      type: String, 
      default: 'text' 
    },
    // Placeholder informativo
    placeholder: { 
      type: String, 
      default: '' 
    },
    // Clases adicionales (Bootstrap ya incluye form-control por defecto)
    inputClass: { 
      type: [String, Object, Array], 
      default: '' 
    },
    // Atributos de estado
    disabled: { 
      type: Boolean, 
      default: false 
    },
    readonly: { 
      type: Boolean, 
      default: false 
    },
    // Limite de caracteres
    maxLength: {
      type: [Number, String],
      default: null
    },
    // Control de errores (Igual a tu DatePicker)
    hasError: { 
      type: Boolean, 
      default: false 
    },
    errorMessage: { 
      type: String, 
      default: '' 
    },
    // Opcional: Textos para el addon de bootstrap
    prefixText: {
      type: String,
      default: ''
    },
    suffixText: {
      type: String,
      default: ''
    },
    // Límite de decimales para type="number"
    maxDecimals: {
      type: [Number, String],
      default: null
    },
    // Formatear miles con comas (requiere type="text")
    formatThousands: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    displayValue() {
      if (this.formatThousands && this.value !== null && this.value !== '') {
        let rawStr = this.value.toString().replace(/,/g, '');
        let parts = rawStr.split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        return parts.join('.');
      }
      return this.value;
    }
  },
  methods: {
    /**
     * Emite el valor al padre para mantener el v-model sincronizado y aplica validación de decimales
     */
    onInput(event) {
      let val = event.target.value;
      
      if (this.formatThousands) {
        val = val.replace(/,/g, '');
      }
      
      // Validación de decimales si es de tipo number o si usa formatThousands
      if ((this.type === 'number' || this.formatThousands) && this.maxDecimals !== null && val) {
        let valStr = val.toString();
        if (valStr.includes('.')) {
          let parts = valStr.split('.');
          let decLimit = parseInt(this.maxDecimals);
          if (parts[1] && parts[1].length > decLimit) {
            val = parts[0] + '.' + parts[1].substring(0, decLimit);
          }
        }
      }
      
      if (this.formatThousands) {
        let formattedParts = val.split('.');
        formattedParts[0] = formattedParts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
        let formatted = formattedParts.join('.');
        
        let cursorStart = event.target.selectionStart;
        let oldLength = event.target.value.length;
        
        event.target.value = formatted;
        
        if (cursorStart !== null) {
          let newLength = formatted.length;
          cursorStart = cursorStart + (newLength - oldLength);
          event.target.setSelectionRange(cursorStart, cursorStart);
        }
      } else if (this.type === 'number') {
        if (event.target.value !== val) {
          event.target.value = val;
        }
      }
      
      this.$emit('input', val);
    },

    /**
     * Emite el evento cuando se presiona la tecla Enter
     */
    onEnter(event) {
      this.$emit('enter', event.target.value);
    },

    /**
     * Filtra caracteres no deseados en inputs numéricos
     */
    onKeyDown(event) {
      if (this.type === 'number' || this.formatThousands) {
        // Permitir teclas de control (copiar, pegar, seleccionar todo)
        if (event.ctrlKey || event.metaKey) {
          this.$emit('keydown', event);
          return;
        }
        
        // Validar si la tecla presionada es un solo caracter (no teclas especiales como Backspace, Tab, etc.)
        if (event.key.length === 1) {
          // Bloquear cualquier caracter que no sea un dígito o un punto/coma
          if (!/[0-9.,]/.test(event.key)) {
            event.preventDefault();
            return;
          }
          
          // Si maxDecimals es 0, bloquear el punto y coma
          if (this.maxDecimals !== null && parseInt(this.maxDecimals) === 0 && /[.,]/.test(event.key)) {
            event.preventDefault();
            return;
          }
        }
      }
      this.$emit('keydown', event);
    },

    /**
     * Emite el evento cuando el input pierde el foco
     */
    onBlur(event) {
      this.$emit('blur', event.target.value);
    }
  },
  inheritAttrs: false
}
</script>
<style scoped>
  /* Ocultar flechas en inputs de tipo number */
  .custom-input[type="number"]::-webkit-inner-spin-button,
  .custom-input[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .custom-input[type="number"] {
    -moz-appearance: textfield;
  }

  .custom-input {
    border: 1px solid #89898995;
    border-radius: 25px !important;
    padding: 12px 15px !important;
    height: auto !important;
    text-align: center;
    background-color: transparent !important;
    color: #45505e !important;
  }

  .support-index.full-layout-content .support-tickets {
    display: none !important;
  }
  .support-index.full-layout-content .support-ticket-content-w {
    flex: 0 0 100% !important;
    max-width: 100% !important;
    display: block;
  }

  .custom-input input::placeholder {
    color: #4d5968 !important;
    font-size: 13px !important;
    opacity: 1;
  }
  .custom-input >>> .vs__dropdown-toggle {
    border: none !important;
  }
  .custom-input >>> .vs__search {
    text-align: center !important;
    color: #546171 !important;
    font-size: 13px;
  }
  .custom-input >>> .vs__selected {
    text-align: center !important;
  }

  .has-prefix {
    border-top-left-radius: 0 !important;
    border-bottom-left-radius: 0 !important;
  }
  .has-suffix {
    border-top-right-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
  
  .prefix-addon {
    border-top-left-radius: 25px !important;
    border-bottom-left-radius: 25px !important;
    background-color: transparent !important;
    border: 1px solid #89898995;
    border-right: none;
    color: #45505e !important;
  }
  .suffix-addon {
    border-top-right-radius: 25px !important;
    border-bottom-right-radius: 25px !important;
    background-color: transparent !important;
    border: 1px solid #89898995;
    border-left: none;
    color: #45505e !important;
  }
</style>
